<script lang="ts">
	import { slide, crossfade, blur } from 'svelte/transition';
	import { quintOut, quintIn } from 'svelte/easing';
	import { applyAction, enhance } from '$app/forms';
	import { onMount } from 'svelte';
	import LoadingBackdrop from '$lib/client/components/LoadingBackdrop.svelte';
	import { page } from '$app/stores';
	import Modal from '$lib/client/components/Modal.svelte';
	import { Embed, type ModalConfig } from '$lib/client/wordpress';
	import { total } from '$lib/client/store';
	import Section from '../../../lib/client/organizational/Section.svelte';
	import { PUBLIC_ENVIRONMENT, PUBLIC_SHOW_RECAPTCHA } from '$env/static/public';
	import * as Store from '$lib/client/store';
	import CenPos, { amount, ref_num } from '$lib/client/components/CenPOS.svelte';
	import { browser } from '$app/environment';
	import { FormController } from '$lib/client/actions';
	import { showDebug } from '$lib/client/store';
	import { DebugSettings, KTIForm, ServerDataValidation } from '$lib/client/form';
	import type { FormConfig } from '$lib/config';
	import Footer, {
		AppendCaptchaData,
		CaptchaValid,
		footer_checked
	} from '$lib/client/components/Footer.svelte';
	import type { SubmitFunction } from '@sveltejs/kit';
	import * as Sentry from '@sentry/sveltekit';
	import Stepper from '$lib/client/components/Stepper.svelte';
	import OrderForm from '$lib/client/components/OrderForm.svelte';
	import Partner from '$lib/client/branding/Partner.svelte';
	import axios from 'axios';
	import type { AxiosProgressEvent } from 'axios';

	let params: Record<string, string> | null = null;
	let footerComp: Footer;
	//const formDef: FormConfig = $page.data as FormConfig;
	KTIForm.form_config = $page.data as FormConfig;
	let showConfirmationPage = false;
	let orderForm: OrderForm | null;
	let emailConfirmationMessage =
		'You should receive a confirmation email with the details of your order.';
	//console.log(formDef);
	let formEl: HTMLDivElement;
	if (browser) {
		Store.formURL.set(KTIForm.form_config.URLName);
	}
	onMount(async () => {
		KTIForm.init();
		if (PUBLIC_ENVIRONMENT == 'dev') {
			showDebug.set(true);
		}
		Store.errorModal.set(KTIForm.error_modal);
		if (KTIForm.form_config.Params) {
			//If we are embedded in wordpress
			if (KTIForm.is_in_wordpress) {
				params = await Embed.getParams();
			} else {
				let urlParams = new URLSearchParams(window.location.search);
				params = Object.fromEntries(urlParams.entries());
				console.log('Params:', params);
			}
		}
	});
	function generateRandomString(length: number): string {
		const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
		let result = '';

		for (let i = 0; i < length; i++) {
			const randomIndex = Math.floor(Math.random() * characters.length);
			result += characters[randomIndex];
		}

		return result;
	}
	const [send, receive] = crossfade({
		duration: 1500,
		easing: quintOut
	});
	// Store.formData.subscribe((data) => {
	// 	formData = data;
	// });
	let saveData = true;
	let showDebugStuff = true;
	let saveToMailChimp = true;

	function startSubmission(objs: {
		action: URL;
		formData: FormData;
		formElement: HTMLFormElement;
		controller: AbortController;
		cancel: () => void;
		submitter: HTMLElement | null;
	}) {
		KTIForm.processing_submission = true;
		KTIForm.loading_backdrop.show();
		//Disable Submit button
		if (objs.submitter) {
			//if submitter is a button, disable it
			if (objs.submitter.tagName == 'BUTTON') {
				let submit_button = objs.submitter as HTMLButtonElement;
				if (!submit_button.disabled) {
					submit_button.setAttribute('disabled', 'true');
				}
			}
		}
	}

	function finishSubmission() {
		KTIForm.processing_submission = false;
		if (!KTIForm.redirecting) {
			KTIForm.loading_backdrop.hide();
		}
	}

	function cancelSubmission(objs: {
		action: URL;
		formData: FormData;
		formElement: HTMLFormElement;
		controller: AbortController;
		cancel: () => void;
		submitter: HTMLElement | null;
	}) {
		finishSubmission();
		objs.cancel();
		//Enable Submit button
		if (objs.submitter) {
			//if submitter is a button, disable it
			if (objs.submitter.tagName == 'BUTTON') {
				let submit_button = objs.submitter as HTMLButtonElement;
				if (submit_button.disabled) {
					submit_button.removeAttribute('disabled');
				}
			}
		}
	}

	const handleSubmit: SubmitFunction = async (objs: {
		action: URL;
		formData: FormData;
		formElement: HTMLFormElement;
		controller: AbortController;
		cancel: () => void;
		submitter: HTMLElement | null;
	}) => {
		const { action, formData, formElement, controller, cancel, submitter } = objs;
		//Cancel SvelteKit's default form submission behavior so that we can track submission status
		cancel();
		if (KTIForm?.processing_submission) {
			return;
		}
		startSubmission(objs);
		KTIForm.form_data = new FormData();

		try {
			//Step 1a
			if (!footer_checked && KTIForm.form_config.RequireConsent) {
				KTIForm.error_modal.Show('Please check the consent box to proceed.');
				cancelSubmission(objs);
				return;
			}
			//Add consent value to data
			KTIForm.form_data.append(
				KTIForm.form_config.URLName + '_consent',
				footer_checked ? 'true' : 'false'
			);
			//Step 1b
			//Check if grecaptcha is valid
			if (!(PUBLIC_ENVIRONMENT == 'dev' || PUBLIC_SHOW_RECAPTCHA === 'false') && !CaptchaValid) {
				KTIForm.error_modal.Show('Please validate captcha');
				cancelSubmission(objs);
				return;
			}
			//Step 2 - if params are required, validate and add them to formData
			//Will not prevent submission if params are not found, but an error will be reported
			if (params !== null) {
				if (!KTIForm.form_config.Params) {
					Sentry.captureMessage(
						"params is not null but form doesn't have Params defined",
						'warning'
					);
				} else {
					let paramInfo = null;
					//this needs to be done during configuration loading and creation
					// try {
					// 	//Check to make sure KTIForm.form_config.Params is valid JSON
					// 	paramInfo = JSON.parse(KTIForm.form_config.Params as string);
					// } catch (err) {
					// 	Sentry.captureException(err);
					// }
					// if (
					// 	typeof KTIForm.form_config.Params == 'object' &&
					// 	Array.isArray(KTIForm.form_config.Params)
					// ) {
					for (let paramInfo of KTIForm.form_config.Params as {
						Name?: string;
						KDColumn?: string;
					}[]) {
						if (paramInfo?.Name && paramInfo?.KDColumn && paramInfo.Name != null) {
							if ($page.url.searchParams.has(paramInfo.Name)) {
								//If not embedded
								let val = $page.url.searchParams.get(paramInfo.Name);
								if (val != null) {
									KTIForm.form_data.append(paramInfo.KDColumn, val);
								} else {
									Sentry.captureMessage(
										`paramInfo.Name: ${paramInfo.Name} not found in url.searchParams`,
										'warning'
									);
								}
							} else if (paramInfo.Name in params) {
								//If embeded
								KTIForm.form_data.append(paramInfo.KDColumn, params[paramInfo.Name]);
							}
						} else {
							Sentry.captureMessage('KTIForm.form_config.Params is not valid', 'warning');
						}
					}
					//}
				}
			}
			//Step 3 - validate components and add data to formData
			//Freeze form while we validate components (don't want people making changes after this point)
			let invalidComps = await KTIForm.getInvalidComponents();
			let CenPOSComp = null;

			function handleInvalidComp() {
				console.log('invalidComps:', invalidComps);
				if (invalidComps[0].focus) {
					invalidComps[0].focus();
				} else {
					console.log('no focus method found for:', invalidComps[0]);
				}
				if (invalidComps[0]?.showError) {
					invalidComps[0].showError();
				}
				cancelSubmission(objs);
			}

			if (invalidComps.length === 0) {
				//Validation will cause all data to be saved to the global formData store so that the processor can find needed data
				for (let section of KTIForm.form_config.Sections) {
					for (let compConfig of section.Components) {
						if (compConfig.Type == 'CenPOS') {
							CenPOSComp = compConfig.Comp;
							if (!(await CenPOSComp.validate())) {
								invalidComps.push(CenPOSComp);
								return handleInvalidComp();
							}
							break;
						}
					}
				}
				await KTIForm.getFormData();
			} else {
				return handleInvalidComp();
			}

			//Step 3.5 - if dup checking is enabled (and other checking)
			//Wait until they have finished validating
			while (KTIForm.validating()) {
				console.log('Waiting for validation to finish:', KTIForm.whichValidating());
				await new Promise((resolve) => setTimeout(resolve, 100));
			}
			console.log('validations:', KTIForm.server_validations);

			if (await KTIForm.failedValidation()) {
				let serviceError = KTIForm.failedServiceCheck();
				let failedDupSubmissionCheck = KTIForm.failedDupSubmissionCheck();
				let failedProductCheck = KTIForm.failedProductCheck();
				let failedInstanceCheck = KTIForm.failedInstanceCheck();
				
				let unknownErrorMsg = `Unknown error has occured. Please try again in a few minutes. If the problem continues, contact customer service at 800-222-0169.`
				let serviceErrorMsg = `We couldn't connect to the server. Please try again in a few minutes. If the problem continues, contact customer service at 800-222-0169.`
				let errorID = undefined

				const tmpTitle = `We're sorry - something went wrong.`

				if (!failedDupSubmissionCheck && !failedProductCheck && !failedInstanceCheck) {
					errorID = generateRandomString(5)
					Sentry.setTag('lastErrorID', errorID)
					KTIForm.error_modal.SetRefStr(errorID)
					if (serviceError) {
						Sentry.captureMessage(`Service Error encountered, see error ID: ${errorID}`, 'fatal')
					} else {
						Sentry.captureMessage(`Unknown Error encountered, see error ID: ${errorID}`, 'fatal')
					}
				} else {
					KTIForm.redirecting = true;
					KTIForm.error_modal.onClose = () => {
						//redirect to home page
						location.href = 'https://www.frogproducts.com/';
					};
				}
				
				if (KTIForm.is_in_wordpress) {
					let modalConf: ModalConfig = {
						title: tmpTitle,
						content: '',
						buttons: [
							{
								text: 'Confirm',
								redirect: KTIForm.redirecting? 'https://www.frogproducts.com/' : undefined, //Will be converted to onclick
								style: {}
							}
						],
						lock_page: true
					};

					if (failedDupSubmissionCheck || failedInstanceCheck) {
						modalConf.content = KTIForm.form_config.DupErrMsg;
					} else if (failedProductCheck) {
						modalConf.content = KTIForm.form_config.HasProductErrMsg;
					} else if (serviceError) {
						modalConf.content = serviceErrorMsg;
					} else {
						modalConf.content = unknownErrorMsg;
					}

					if (errorID) {
						modalConf.content += `<br>Error: ${errorID}`
					}

					console.log('sending showModal', modalConf);
					Embed.send({ showModal: modalConf });
				} else {
					KTIForm.error_modal.SetTmpTitle(tmpTitle)

					if (failedDupSubmissionCheck || failedInstanceCheck) {
						KTIForm.error_modal.Show(KTIForm.form_config.DupErrMsg);
					} else if (failedProductCheck) {
						KTIForm.error_modal.Show(KTIForm.form_config.HasProductErrMsg);
					} else if (serviceError) {
						KTIForm.error_modal.Show(serviceErrorMsg)
					} else {
						KTIForm.error_modal.Show(unknownErrorMsg)
					}

					window.scrollTo(0, 0);
				}

				cancelSubmission(objs);
				return;
			}
			//Step 4: All data is valid at this point
			//Call all components.onSubmit() method
			for (let section of KTIForm.form_config.Sections) {
				for (let compConfig of section.Components) {
					if (compConfig.Comp && 'onSubmit' in compConfig.Comp) {
						let success = await compConfig.Comp.onSubmit();
						if (!success) {
							cancelSubmission(objs);
							return;
						}
					}
				}
			}
			//Adds recaptcha information to the submission data to be validated on the server
			if (PUBLIC_ENVIRONMENT == 'dev' || PUBLIC_SHOW_RECAPTCHA === 'false') {
				//Add mailchimp bypass
				if (!DebugSettings.save_to_mailchimp) {
					KTIForm.form_data.append('skip_mailchimp', 'true');
				}
			} else {
				AppendCaptchaData(KTIForm.form_data);
			}
			console.log('saveData:', saveData);
			if (!saveData) {
				cancelSubmission(objs);
				return;
			}
			//Let FormController process the final form data object.
			FormController.process_form_data(KTIForm.form_data);

			const allKeys = Array.from(formData.keys());
			for (let key of allKeys) {
				formData.delete(key);
			}
			for (let [key, value] of KTIForm.form_data.entries()) {
				formData.append(key, value);
			}
			// Convert formData to a JSON string
			console.log('Submit - formData');
			console.log(Object.fromEntries(formData.entries()));

			// Convert KTIForm.form_data to a JSON string
			console.log('Submit - KTIForm.form_data');
			console.log(Object.fromEntries(KTIForm.form_data.entries()));

			let uploadProgress = 0;
			let hasProgressStarted = false;
			let lastProgressTime = Date.now();
			const CONNECTION_TIMEOUT = 5000;
			const PROGRESS_TIMEOUT = 5000;
			let connectionTimeout: NodeJS.Timeout;
			let progressTimeout: NodeJS.Timeout | undefined;
			const abortController = new AbortController();

			connectionTimeout = setTimeout(() => {
				if (!hasProgressStarted) {
					abortController.abort();
					KTIForm.error_modal.Show(
						'No server connection within 5 seconds. Please check your network and try again.'
					);
					KTIForm.loading_backdrop.hide();
					KTIForm.processing_submission = false;
				}
			}, CONNECTION_TIMEOUT);

			try {
				const response = await axios.post(action.toString(), formData, {
					signal: abortController.signal,
					onUploadProgress: (progressEvent: AxiosProgressEvent) => {
						if (progressEvent.lengthComputable) {
							hasProgressStarted = true;
							clearTimeout(connectionTimeout);

							if (progressEvent.total === undefined) {
								console.log('progressEvent.total is undefined');
								return;
							}
							const newProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
							if (newProgress > uploadProgress) {
								uploadProgress = newProgress;
								lastProgressTime = Date.now();
							}

							clearTimeout(progressTimeout);
							progressTimeout = setTimeout(() => {
								if (uploadProgress < 100 && Date.now() - lastProgressTime >= PROGRESS_TIMEOUT) {
									abortController.abort();
									KTIForm.error_modal.Show('Lost server connection. Please try again.');
									KTIForm.loading_backdrop.hide();
									KTIForm.processing_submission = false;
								}
							}, PROGRESS_TIMEOUT);
						}
					}
				});

				clearTimeout(connectionTimeout);
				if (progressTimeout !== undefined) {
					clearTimeout(progressTimeout);
				}
				KTIForm.loading_backdrop.hide();
				KTIForm.processing_submission = false;

				if (response.status === 200) {
					const result = JSON.parse(JSON.parse(response.data.data)[0]);
					console.log(response);
					console.log(response.data.data);
					console.log(result);
					if (response.data?.type === 'success') {
						//Server will return 200 or "success" on errors!!
						//Check the result.data to see if it is a success or failure
						console.log('Data:', result);
						console.log('transaction_data:', result?.transaction_data);
						console.log('id_counter:', result?.transaction_data?.id_counter);
						if (result?.success === true) {
							//Success
							if (CenPOSComp) {
								//loop through components looking for order form
								for (let section of KTIForm.form_config.Sections) {
									for (let compConfig of section.Components) {
										if (compConfig.Type == 'OrderForm') {
											orderForm = compConfig.Comp;
											break;
										}
									}
								}
								showConfirmationPage = true;
								// if (result?.data?.transaction_data?.dup_email) {
								// 	emailConfirmationMessage =
								// 		'For <strong>multiple orders</strong> from the same email address expect a confirmation email from our customer service team within <strong>1-3 business days</strong>.';
								// }

								return;
							}
							if (KTIForm.form_config.SuccessPage) {
								KTIForm.redirecting = true;
								if (KTIForm.is_in_wordpress) {
									Embed.send({ redirect: KTIForm.form_config.SuccessPage });
								} else {
									location.href = KTIForm.form_config.SuccessPage;
								}
							} else if (result?.transaction_data) {
								let invoiceNum = result?.transaction_data?.id_counter;
								//let amount = result?.data?.transaction_data?.amount;
								if (invoiceNum) {
									KTIForm.error_modal.onClose = () => {
										//reload page
										location.reload();
									};
									KTIForm.error_modal.SetTmpTitle('Submission Successful');
									KTIForm.error_modal.Show(
										`Invoice Number for Credit Card Payment: ${invoiceNum}<br>Total Amount: $${$total.toFixed(
											2
										)}`
									);
								}
							}
							if (!KTIForm.is_in_wordpress) {
								KTIForm.show_success_page = true;
							}
							// await applyAction(result);
						} else {
							//Failure

							if (result?.errors?.Email) {
								if (result?.errors?.Email.includes('Invalid email address')) {
									//let errorID = generateRandomString(5);
									//KTIForm.error_modal.SetRefStr(errorID);
									KTIForm.error_modal.Show(
										'This email address you entered is not being accepted by our email service provider. Please check that this is a valid email that allows incoming emails.'
									);
									return;
								} else if (
									//MailChimp Subscription Required
									result?.errors?.Email.includes('Resubscribe') &&
									KTIForm.form_config.MailChimpRequireSubscription
								) {
									//let errorID = generateRandomString(5);
									//KTIForm.error_modal.SetRefStr(errorID);
									KTIForm.error_modal.Show(
										'It appears you have signed up for a program with King Technology and previously unsubscribed from our mailing list. To sign up, please re-subscribe to our list by clicking a link in the confirmation email sent to you shortly. <br/><br/>Once you have confirmed, return to this form and click submit to complete sign up.  If you have any questions please contact 800-222-0169 or <a href="mailto:customer.service@kingtechnology.com">customer.service@kingtechnology.com</a>.'
									);
									return;
								} else {
									let errorID = generateRandomString(5);
									KTIForm.error_modal.SetRefStr(errorID);
									KTIForm.error_modal.Show(
										'An error occurred. Please try again later or contact customer service at 800-222-0169.'
									);
									Sentry.captureMessage(
										'Submission server error (' + errorID + '): ' + JSON.stringify(result)
									);
								}
							} else if (result?.error?.message) {
								KTIForm.error_modal.Show(result.error.message);
							} else {
								let errorID = generateRandomString(5);
								KTIForm.error_modal.SetRefStr(errorID);
								KTIForm.error_modal.Show(
									'An error occurred. Please try again later or contact customer service at 800-222-0169.'
								);
								Sentry.captureMessage(
									'Submission server error (' + errorID + '): ' + JSON.stringify(result)
								);
							}
						}
					} else {
						Sentry.captureMessage('result.type is not success', 'error');
						let errorID = generateRandomString(5);
						KTIForm.error_modal.SetRefStr(errorID);
						KTIForm.error_modal.Show(
							'An error occurred. Please try again later or contact customer service at 800-222-0169.'
						);
						Sentry.captureMessage(
							'Submission server error (' + errorID + '): ' + JSON.stringify(response)
						);
					}
					finishSubmission();
				} else {
					console.log(response);
					KTIForm.error_modal.Show('Submission failed. Please try again.');
				}
			} catch (err) {
				clearTimeout(connectionTimeout);
				clearTimeout(progressTimeout);
				KTIForm.loading_backdrop.hide();
				KTIForm.processing_submission = false;
				if (!axios.isCancel(err)) {
					KTIForm.error_modal.Show('An error occurred. Please try again later.');
				}
			}

			// try {
			// 	// Use axios to send the form data with progress tracking
			// 	const response = await axios.post(action.toString(), formData, {
			// 		headers: {
			// 			'Content-Type': 'multipart/form-data' // Ensure proper handling of FormData
			// 		},
			// 		onUploadProgress: (progressEvent) => {
			// 			if (progressEvent.lengthComputable) {
			// 				console.log('progress');
			// 				if (progressEvent.total === undefined) {
			// 					console.log('progressEvent.total is undefined');
			// 					return;
			// 				}
			// 				submissionProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
			// 				console.log(`Upload progress: ${submissionProgress}%`);
			// 			}
			// 		}
			// 	});

			// 	// Submission completed successfully
			// 	isConnected = false;
			// 	isComplete = true;
			// 	console.log('Server response:', response.data);
			// 	const result = response.data.data;
			// 	console.log('Server response data:', result);

			// 	// Optionally, mimic enhance's response handling
			// 	// You could redirect or update state here as needed
			// } catch (err) {
			// 	// Handle errors
			// 	isConnected = false;
			// 	submissionError =
			// 		err instanceof Error ? err.message : 'An error occurred during submission';
			// 	console.error('Submission failed:', err);
			// }
			// return;

			// //Handle server response (not gaurenteed to get a response)
			// return async (opt) => {
			// 	const result = opt.result;
			// 	if (!(PUBLIC_ENVIRONMENT == 'dev') && PUBLIC_SHOW_RECAPTCHA !== 'false') {
			// 		window.grecaptcha.reset();
			// 	}
			// 	console.log(result);
			// 	if (result.type === 'success') {
			// 		//Server will return 200 or "success" on errors!!
			// 		//Check the result.data to see if it is a success or failure
			// 		console.log('Data:', result.data);
			// 		console.log('transaction_data:', result?.data?.transaction_data);
			// 		console.log('id_counter:', result?.data?.transaction_data?.id_counter);
			// 		if (result.data?.success) {
			// 			//Success
			// 			if (CenPOSComp) {
			// 				//loop through components looking for order form
			// 				for (let section of KTIForm.form_config.Sections) {
			// 					for (let compConfig of section.Components) {
			// 						if (compConfig.Type == 'OrderForm') {
			// 							orderForm = compConfig.Comp;
			// 							break;
			// 						}
			// 					}
			// 				}
			// 				showConfirmationPage = true;
			// 				// if (result?.data?.transaction_data?.dup_email) {
			// 				// 	emailConfirmationMessage =
			// 				// 		'For <strong>multiple orders</strong> from the same email address expect a confirmation email from our customer service team within <strong>1-3 business days</strong>.';
			// 				// }

			// 				return;
			// 			}
			// 			if (KTIForm.form_config.SuccessPage) {
			// 				if (KTIForm.is_in_wordpress) {
			// 					Embed.send({ redirect: KTIForm.form_config.SuccessPage });
			// 				} else {
			// 					location.href = KTIForm.form_config.SuccessPage;
			// 				}
			// 			} else if (result?.data?.transaction_data) {
			// 				let invoiceNum = result?.data?.transaction_data?.id_counter;
			// 				//let amount = result?.data?.transaction_data?.amount;
			// 				if (invoiceNum) {
			// 					KTIForm.error_modal.onClose = () => {
			// 						//reload page
			// 						location.reload();
			// 					};
			// 					KTIForm.error_modal.SetTmpTitle('Submission Successful');
			// 					KTIForm.error_modal.Show(
			// 						`Invoice Number for Credit Card Payment: ${invoiceNum}<br>Total Amount: $${$total.toFixed(
			// 							2
			// 						)}`
			// 					);
			// 				}
			// 			}
			// 			if (!KTIForm.is_in_wordpress) {
			// 				KTIForm.show_success_page = true;
			// 			}
			// 			await applyAction(result);
			// 		} else {
			// 			//Failure

			// 			if (result.data?.errors?.Email) {
			// 				if (result.data?.errors?.Email.includes('Invalid email address')) {
			// 					//let errorID = generateRandomString(5);
			// 					//KTIForm.error_modal.SetRefStr(errorID);
			// 					KTIForm.error_modal.Show(
			// 						'This email address you entered is not being accepted by our email service provider. Please check that this is a valid email that allows incoming emails.'
			// 					);
			// 					return;
			// 				} else if (
			// 					//MailChimp Subscription Required
			// 					result.data?.errors?.Email.includes('Resubscribe') &&
			// 					KTIForm.form_config.MailChimpRequireSubscription
			// 				) {
			// 					//let errorID = generateRandomString(5);
			// 					//KTIForm.error_modal.SetRefStr(errorID);
			// 					KTIForm.error_modal.Show(
			// 						'It appears you have signed up for a program with King Technology and previously unsubscribed from our mailing list. To sign up, please re-subscribe to our list by clicking a link in the confirmation email sent to you shortly. <br/><br/>Once you have confirmed, return to this form and click submit to complete sign up.  If you have any questions please contact 800-222-0169 or <a href="mailto:customer.service@kingtechnology.com">customer.service@kingtechnology.com</a>.'
			// 					);
			// 					return;
			// 				} else {
			// 					let errorID = generateRandomString(5);
			// 					KTIForm.error_modal.SetRefStr(errorID);
			// 					KTIForm.error_modal.Show(
			// 						'An error occurred. Please try again later or contact customer service at 800-222-0169.'
			// 					);
			// 					Sentry.captureMessage(
			// 						'Submission server error (' + errorID + '): ' + JSON.stringify(result.data)
			// 					);
			// 				}
			// 			} else if (result.data?.error?.message) {
			// 				KTIForm.error_modal.Show(result.data.error.message);
			// 			} else {
			// 				let errorID = generateRandomString(5);
			// 				KTIForm.error_modal.SetRefStr(errorID);
			// 				KTIForm.error_modal.Show(
			// 					'An error occurred. Please try again later or contact customer service at 800-222-0169.'
			// 				);
			// 				Sentry.captureMessage(
			// 					'Submission server error (' + errorID + '): ' + JSON.stringify(result.data)
			// 				);
			// 			}
			// 		}
			// 	} else {
			// 		Sentry.captureMessage('result.type is not success', 'error');
			// 		let errorID = generateRandomString(5);
			// 		KTIForm.error_modal.SetRefStr(errorID);
			// 		KTIForm.error_modal.Show(
			// 			'An error occurred. Please try again later or contact customer service at 800-222-0169.'
			// 		);
			// 		Sentry.captureMessage(
			// 			'Submission server error (' + errorID + '): ' + JSON.stringify(opt)
			// 		);
			// 	}
			// 	finishSubmission();
			// 	// if (result.type === 'error') {
			// 	// 	await applyAction(result);
			// 	// } else if (result.type === 'success' || result.type === 'failure') {
			// 	// 	$page.data = result.data;
			// 	// 	form = result.data;
			// 	// }
			// 	// KTIForm.loading_backdrop.hide();
			// 	// console.log('result:', result);

			// 	// if ('data' in result) {
			// 	// 	data = result.data;
			// 	// }
			// 	// if ('form' in result) {
			// 	// 	form = result.type === 'success' ? { success: true } : null;
			// 	// }
			// 	// `result` is an `ActionResult` object
			// 	// `update` is a function which triggers the default logic that would be triggered if this callback wasn't set
			// };
		} catch (err) {
			console.error(err);
			KTIForm.error_modal.Show('An error occurred. Please try again later.');
		} finally {
			KTIForm.processing_submission = false;
			KTIForm.loading_backdrop.hide();
			if (!(PUBLIC_ENVIRONMENT == 'dev') && PUBLIC_SHOW_RECAPTCHA !== 'false') {
				window.grecaptcha.reset();
			}
		}
	};

	let showDebugSection: boolean = DebugSettings.show_debug;
</script>

<Modal bind:this={KTIForm.error_modal} />
{#if KTIForm.form_config.Branding === 'Partner'}
	<Partner header={true} />
{/if}
{#if showConfirmationPage}
	<div class="container frame">
		<div class="text-center" style="width: 100%; margin-top: 50px; display:block">
			<div style="margin-top: 20px; font-size: 20px; margin-bottom: 0px">
				<h2>Order Successful</h2>
			</div>
			<br />
			<img src="/success.png" alt="success" /><br />
			<strong style="margin-top: 20px">Thank you!</strong><br />
			<hr />
			<h3>Transaction Details</h3>
			<div class="d-flex justify-content-start" style="width: 50%; margin:auto; margin-top:5px">
				<div>Amount: <strong>${amount}</strong></div>
			</div>
			<div class="d-flex justify-content-start" style="width: 50%; margin:auto">
				<div>Reference Number: <strong>{ref_num}</strong></div>
			</div>
			<!-- <div class="d-flex justify-content-start" style="width: 50%; margin:auto">
				<div>Transaction Date:</div>
			</div> -->
			<hr />
			<div style="text-align: left;">
				{@html orderForm.Config.ConfirmationHTML}<br /><br />
			</div>
		</div>
	</div>
{:else if KTIForm.show_success_page}
	{#if KTIForm.form_config.SuccessHTML}
		{@html KTIForm.form_config.SuccessHTML}
	{:else}
		<div
			in:slide={{ delay: 100, easing: quintIn }}
			class="alert alert-success mt-4"
			style="max-width: 800px;  margin-left: auto;  margin-right: auto;"
		>
			Thank you for your submission!
			<!--     
		Thank you! Your information has been received. Please save a copy of this confirmation page for
		your records.<br /><br />
		As a reminder, this form authorizes you to sell only on your “Permissible Public Website” that you
		disclosed with King Technology through this form.
		<b>All online marketplace sales are strictly prohibited.</b><br /><br />
		Thank you for your support of King Technology and our products. -->
		</div>
	{/if}
{:else}
	<div
		id="entireForm"
		class="container frame"
		transition:blur={{ amount: 20, duration: 600 }}
		bind:this={formEl}
	>
		<form use:enhance={handleSubmit} autocomplete="on" method="POST" enctype="multipart/form-data">
			{#if KTIForm.form_config.Title}
				<div class="justify-content-center" style="text-align: center; padding-top: 10px">
					<h1>{KTIForm.form_config.Title}</h1>
				</div>
			{/if}
			{#if KTIForm.form_config.SectionType == 'Stepper'}
				<Stepper sections={KTIForm.form_config.Sections} />
			{:else}
				{#each KTIForm.form_config.Sections as section, i}
					<Section
						bind:section
						SectionType={KTIForm.form_config.SectionType}
						LastSection={i == KTIForm.form_config.Sections.length - 1}
						TotalSteps={KTIForm.form_config.Sections.length}
					/>
				{/each}
			{/if}
		</form>
	</div>
{/if}
{#if KTIForm.form_config.Branding === 'Partner'}
	<Partner footer={true} />
{/if}
{#if DebugSettings.debug_enabled && showDebugSection}
	<div class="container" style="width: 600px">
		<div class="form-check">
			<input
				class="form-check-input"
				type="checkbox"
				id="submitData"
				bind:checked={DebugSettings.save_to_db}
			/>
			<label class="form-check-label" for="submitData">
				Submit data to server: {saveData}
			</label>
		</div>

		<div class="form-check">
			<input
				class="form-check-input"
				type="checkbox"
				id="saveToMailChimp"
				bind:checked={DebugSettings.save_to_mailchimp}
			/>
			<label class="form-check-label" for="saveToMailChimp"> Save to Mailchimp </label>
		</div>

		<div class="form-check">
			<input
				class="form-check-input"
				type="checkbox"
				id="showDebug"
				bind:checked={showDebugSection}
			/>
			<label class="form-check-label" for="showDebug"> Show Debug Section </label>
		</div>

		<div>
			<strong>Debug Info:</strong><br />
			<pre>{JSON.stringify(KTIForm.form_data)}</pre>
			Validations: {ServerDataValidation.toString()}<br />
			IsEmbedded: {KTIForm.is_in_wordpress}<br />
		</div>
	</div>
{/if}
<LoadingBackdrop bind:this={KTIForm.loading_backdrop} />

<style>
	.stepper-active .stepper-head .stepper-head-text {
		font-weight: 600;
	}
	.stepper-active .stepper-content {
		display: block;
	}
</style>
